import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AboutSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-primary);
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImageWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid var(--color-accent);
    z-index: 1;
  }
  
  @media (max-width: 992px) {
    max-height: 400px;
  }
`;

const AboutImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 2;
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h2.attrs({
  as: motion.h2
})`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -10px;
    width: 60px;
    height: 3px;
    background-color: var(--color-accent);
  }
`;

const SectionSubtitle = styled.span.attrs({
  as: motion.span
})`
  font-size: 1rem;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  display: block;
`;

const AboutText = styled.p.attrs({
  as: motion.p
})`
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--color-text-secondary);
`;

const AboutFeatures = styled.ul.attrs({
  as: motion.ul
})`
  list-style: none;
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const AboutFeatureItem = styled.li.attrs({
  as: motion.li
})`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  &:before {
    content: '✓';
    color: var(--color-accent);
    margin-right: 10px;
    font-weight: bold;
  }
`;

const About: React.FC = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <AboutSection id="about" ref={ref}>
      <AboutContainer>
        <AboutImageWrapper>
          <AboutImage src="/images/about-image.jpg" alt="Elegantes ternos e smokings" />
        </AboutImageWrapper>
        
        <AboutContent
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <SectionSubtitle variants={itemVariants}>Sobre Nós</SectionSubtitle>
          <SectionTitle variants={itemVariants}>My Dream Suits</SectionTitle>
          <AboutText variants={itemVariants}>
            Somos especialistas em moda masculina de alta qualidade, oferecendo ternos e smokings perfeitos para os momentos mais importantes da sua vida. Com anos de experiência no mercado, entendemos que cada ocasião merece um traje que combine elegância, conforto e personalidade.
          </AboutText>
          <AboutText variants={itemVariants}>
            Nossa missão é proporcionar a melhor experiência em aluguel e venda de trajes formais masculinos, com atendimento personalizado e produtos de excelência que valorizam a sua presença em qualquer evento.
          </AboutText>
          
          <AboutFeatures variants={containerVariants}>
            <AboutFeatureItem variants={itemVariants}>Ternos Premium</AboutFeatureItem>
            <AboutFeatureItem variants={itemVariants}>Smokings Exclusivos</AboutFeatureItem>
            <AboutFeatureItem variants={itemVariants}>Atendimento Personalizado</AboutFeatureItem>
            <AboutFeatureItem variants={itemVariants}>Ajustes Perfeitos</AboutFeatureItem>
            <AboutFeatureItem variants={itemVariants}>Variedade de Cores</AboutFeatureItem>
            <AboutFeatureItem variants={itemVariants}>Tecidos de Qualidade</AboutFeatureItem>
          </AboutFeatures>
        </AboutContent>
      </AboutContainer>
    </AboutSection>
  );
};

export default About; 